import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import HeroSection from './components/HeroSection'
import CategorySection from './components/CategorySection'
import HotRecommendSection from './components/HotRecommendSection'
import ReviewSection from './components/ReviewSection'
import StatsSection from './components/StatsSection'
import CTASection from './components/CTASection'
import ScrollReveal from '@/components/base/ScrollReveal'
import { comics } from '@/mocks/comics'

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const newReleases = comics.filter(c => c.isNew).slice(0, 8)
  const ranking = [...comics].sort((a, b) => b.views.localeCompare(a.views)).slice(0, 10)

  return (
    <>
      <Navbar />

      <main>
        <HeroSection />

        <CategorySection />

        <HotRecommendSection />

        {/* 新作速递 */}
        <section className="py-16 md:py-24 bg-background-100" aria-labelledby="new-releases-title">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <ScrollReveal direction="up" className="flex items-center justify-between mb-10">
              <div>
                <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium mb-3">
                  最新上架
                </span>
                <h2 id="new-releases-title" className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  新作速递
                </h2>
              </div>
              <Link
                to="/category/romance"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </Link>
            </ScrollReveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {newReleases.concat(comics.slice(5, 9)).map((comic, index) => (
                <ScrollReveal
                  key={`new-${comic.id}`}
                  direction="up"
                  delay={index * 60}
                  duration={500}
                >
                  <Link
                    to={`/article/${comic.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/50 hover:border-primary-200 hover:-translate-y-1.5 hover:shadow-[0_8px_30px_-6px_oklch(var(--primary-500)/0.12)] active:translate-y-0 transition-all duration-400 ease-out cursor-pointer block"
                  >
                    <div className="relative w-full overflow-hidden" style={{ height: '240px' }}>
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ease-out"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          comic.isNew
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-foreground-900/70 text-background-50'
                        }`}>
                          {comic.isNew ? 'NEW' : comic.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 mb-1 line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-2">{comic.author}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{comic.updateInfo}</span>
                        <span className="flex items-center gap-0.5 text-xs font-medium text-foreground-700">
                          <span className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                            <i className="ri-star-fill text-[10px]"></i>
                          </span>
                          {comic.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* 人气排行 TOP10 */}
        <section className="py-16 md:py-24 bg-background-50" aria-labelledby="ranking-title">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <ScrollReveal direction="up" className="mb-10">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium mb-3">
                排行榜
              </span>
              <h2 id="ranking-title" className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                人气排行 TOP10
              </h2>
            </ScrollReveal>

            <div className="bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden">
              {ranking.map((comic, index) => (
                <ScrollReveal
                  key={`rank-${comic.id}`}
                  direction="left"
                  delay={index * 50}
                  duration={450}
                >
                  <Link
                    to={`/article/${comic.id}`}
                    className={`flex items-center gap-4 p-4 md:p-5 hover:bg-background-100 hover:-translate-x-1 transition-all duration-300 ease-out cursor-pointer ${
                      index < ranking.length - 1 ? 'border-b border-background-200/50' : ''
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                      index === 0 ? 'bg-primary-500 text-background-50' :
                      index === 1 ? 'bg-accent-500 text-background-50' :
                      index === 2 ? 'bg-secondary-500 text-background-50' :
                      'bg-background-200 text-foreground-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="w-12 h-16 rounded-lg overflow-hidden shrink-0 group-hover:scale-105 transition-transform duration-300">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-sm md:text-base font-bold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors duration-200">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 shrink-0">
                      <span className="inline-block px-2 py-0.5 bg-background-200 rounded-full text-xs text-foreground-600">
                        {comic.category}
                      </span>
                      <span className={`text-xs font-medium ${comic.status === '连载中' ? 'text-primary-600' : 'text-foreground-500'}`}>
                        {comic.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-xs"></i>
                      </span>
                      <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <ReviewSection />

        <StatsSection />

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-background-100" aria-labelledby="faq-title">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <ScrollReveal direction="up" className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium mb-3">
                常见问题
              </span>
              <h2 id="faq-title" className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                读者最关心的问题
              </h2>
            </ScrollReveal>

            <div className="space-y-3">
              {[
                { q: '韩漫漫画网站的作品都是正版吗？', a: '是的，我们与韩国多家顶级漫画平台建立了官方合作关系，所有作品均获得正版授权。我们坚持为正版内容付费，保障创作者的合法权益，同时为读者提供最高清的阅读体验。' },
                { q: '多久更新一次？更新速度如何？', a: '我们与韩国的更新时间保持同步，大部分热门连载作品每日更新。专业翻译团队7×24小时不间断工作，确保中文版本在第一时间与韩国读者同步上线。' },
                { q: '需要付费才能阅读吗？', a: '网站提供大量免费阅读内容！新用户注册即可获得7天全站免费阅读权益。部分独家内容需要会员订阅，但我们始终保证有充足的免费内容供所有读者阅读。' },
                { q: '支持哪些设备阅读？', a: '韩漫漫画网站采用响应式设计，完美支持PC、平板和手机端阅读。无论你使用什么设备，都能获得最佳的阅读体验。推荐使用Chrome或Safari浏览器。' },
                { q: '如何找到我喜欢的类型？', a: '网站提供详细的分类导航系统，涵盖浪漫、动作、奇幻、BL、悬疑、剧情等十余个大类。你可以通过顶部分类菜单快速定位自己喜欢的类型，也可以使用搜索功能直接找到心仪的作品。' },
              ].map((faq, i) => (
                <ScrollReveal key={i} direction="up" delay={i * 80} duration={450}>
                  <details className="group bg-background-50 rounded-2xl border border-background-200/50 hover:border-background-300/70 transition-all duration-300">
                    <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                      <span className="font-heading text-sm md:text-base font-semibold text-foreground-900 pr-4">
                        {faq.q}
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-300">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '韩漫漫画网站 - 首页',
        description: '韩国漫画专区，热门韩漫推荐与免费韩漫大全',
        url: 'http://tenford.cn/',
        isPartOf: {
          '@type': 'WebSite',
          name: '韩漫漫画网站',
          url: 'http://tenford.cn/',
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://tenford.cn/' },
          ],
        },
      })}} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: '韩漫漫画网站的作品都是正版吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，我们与韩国多家顶级漫画平台建立了官方合作关系，所有作品均获得正版授权。' } },
          { '@type': 'Question', name: '多久更新一次？更新速度如何？', acceptedAnswer: { '@type': 'Answer', text: '我们与韩国的更新时间保持同步，大部分热门连载作品每日更新。' } },
          { '@type': 'Question', name: '需要付费才能阅读吗？', acceptedAnswer: { '@type': 'Answer', text: '网站提供大量免费阅读内容！新用户注册即可获得7天全站免费阅读权益。' } },
          { '@type': 'Question', name: '支持哪些设备阅读？', acceptedAnswer: { '@type': 'Answer', text: '完美支持PC、平板和手机端阅读，采用响应式设计。' } },
          { '@type': 'Question', name: '如何找到我喜欢的类型？', acceptedAnswer: { '@type': 'Answer', text: '网站提供详细的分类导航系统，涵盖浪漫、动作、奇幻、BL等十余个大类。' } },
        ],
      })}} />
    </>
  )
}